import { useContext, useState } from 'react'
import { useTranslation } from 'libs/i18n'
import { Link, useSearchParams } from 'libs/router'
import { SelectClickTypes } from '@table-library/react-table-library/select'
import { routes } from 'config/routes'
import {
	ActionsSelect,
	BookingEngineTabs,
	CardHeader,
	CardWrapper,
	ConfirmModal,
	CtaLink,
	DeleteConfirmModal,
	EmptyTable,
	FiltersBar,
	FiltersBarField,
	FormatModal,
	Header,
	SearchInput,
	Select
} from 'shared/components'
import {
	useDevice,
	useFetchInfinite,
	useFilter,
	useModal,
	useSearch,
	useTableSelect
} from 'shared/hooks'
import { IOption } from 'interfaces/select.interface'
import { AuthContext, StoreSettingsContext } from 'store'
import { useFetchCoupons } from './queries/useFetchCoupons'
import { useActionFilters } from './hooks/useActionFilters'
import { useContextMenuOptions } from './hooks/useContextMenuOptions'
import { useStatusOptions } from './hooks/useStatusOptions'
import { useDeleteCouponMutation } from './mutations/useDeleteCouponMutation'
import { useDeleteCouponsMutation } from './mutations/useDeleteCouponsMutation'
import { useExportCouponsMutation } from './mutations/useExportCouponsMutation'
import { useToggleCouponActiveMutation } from './mutations/useToggleCouponActiveMutation'
import { TableSection } from './organisms/TableSection'

const CouponsPage = () => {
	const { t } = useTranslation()
	const [searchParams] = useSearchParams()
	const { isMobileOrTablet } = useDevice()
	const { handleSearch } = useSearch({ name: 'search' })
	const { handleFilter } = useFilter()
	const { auth } = useContext(AuthContext)
	const storeCtx = useContext(StoreSettingsContext)
	const [checkedCoupons, setCheckedCoupons] = useState<number[]>([])
	const [idToBeRemoved, setIdToBeRemoved] = useState<number | null>(null)
	const [chosenExportFormat, setChosenExportFormat] = useState<number | null>(null)
	const statusOptions = useStatusOptions()
	const { data: boothTypes, fetchNextPage: fetchNextAreaTypes } = useFetchInfinite({
		name: 'fetchAreaTypes',
		endpoint: '/booth-types/',
		enabled: !!auth.accessToken
	})
	const boothTypeOptions: IOption[] = (boothTypes?.pages ?? [])
		.flatMap((page: any) => page.results)
		.map((item: any) => ({ label: item.name, value: item.id }))
	const { coupons, isCouponsLoading } = useFetchCoupons()
	const { select } = useTableSelect({ nodes: coupons?.data?.results ?? [] }, onSelectChange, {
		clickType: SelectClickTypes.ButtonClick
	})
	const clearSelection = () => select.fns.onRemoveAll()
	const [actionsFilter] = useActionFilters(!checkedCoupons.length)

	const {
		isOpen: isDeleteModalOpen,
		openModal: openDeleteModal,
		closeModal: closeDeleteModal
	} = useModal()
	const {
		isOpen: isBulkDeleteModalOpen,
		openModal: openBulkDeleteModal,
		closeModal: closeBulkDeleteModal
	} = useModal()
	const {
		isOpen: isExportModalOpen,
		openModal: openExportModal,
		closeModal: closeExportModal
	} = useModal()
	const {
		isOpen: isFormatModalOpen,
		openModal: openFormatModal,
		closeModal: closeFormatModal
	} = useModal()

	const { couponDeleteMutation } = useDeleteCouponMutation({
		id: idToBeRemoved,
		onSettled: closeDeleteModal
	})

	const { couponsDeleteMutation } = useDeleteCouponsMutation({
		ids: checkedCoupons,
		onSettled: () => {
			clearSelection()
			closeBulkDeleteModal()
		}
	})

	const { exportCouponsMutation, isExportCouponsLoading } = useExportCouponsMutation({
		chosenFormat: chosenExportFormat,
		onSuccess: clearSelection,
		onSettled: () => {
			closeFormatModal()
			setChosenExportFormat(null)
		}
	})

	const { toggleCouponActiveMutation } = useToggleCouponActiveMutation()

	function onSelectChange(_action: any, state: any) {
		setCheckedCoupons(state.ids)
	}

	const handleDelete = (id: number) => {
		setIdToBeRemoved(id)
		openDeleteModal()
	}

	const getMenuOptions = useContextMenuOptions({
		onToggleActive: (id, is_active) => toggleCouponActiveMutation({ id, is_active }),
		onDelete: handleDelete
	})

	const handleAction = (action: string) => {
		switch (action) {
			case 'export':
				openExportModal()
				break
			case 'delete':
				openBulkDeleteModal()
				break
		}
	}

	const handleExportModal = () => {
		closeExportModal()
		openFormatModal()
	}

	const handleFormatChange = (data: IOption) => {
		setChosenExportFormat(data?.value?.toString() ? (data.value as number) : null)
	}

	const renderContent = () => {
		if (isCouponsLoading) return <EmptyTable isLoading />
		if (!coupons?.data?.results?.length)
			return <EmptyTable text={searchParams.toString() ? t('No results') : t('No coupons yet')} />

		return <TableSection coupons={coupons} select={select} getMenuOptions={getMenuOptions} />
	}

	return (
		<>
			<Header
				title={t('Booking engine')}
				tabs={<BookingEngineTabs />}
				actions={
					!isMobileOrTablet && (
						<ActionsSelect options={actionsFilter.options} onChange={handleAction} />
					)
				}
			/>
			<div className="px-5 lg:flex lg:max-h-[calc(100vh-113.5px-4rem)] lg:flex-col lg:px-0">
				{storeCtx?.storeSettings?.coupons_enabled === false && (
					<p className="mb-5 shrink-0 rounded-lg border border-warning/40 bg-warning-soft px-4 py-3 text-[13px] text-foreground">
						{t("Coupons are disabled for this store; shoppers can't use them.")}{' '}
						<Link
							className="font-semibold text-primary underline-offset-2 hover:underline"
							to={routes.settingsIndex}>
							{t('Go to settings')}
						</Link>
					</p>
				)}
				<CardWrapper className="lg:flex lg:min-h-0 lg:flex-1 lg:flex-col">
					<CardHeader title={t('Coupons')} description={t('Codes customers can use when booking.')}>
						<CtaLink to={routes.couponAdd} text={t('Add code')} />
					</CardHeader>
					<FiltersBar>
						<FiltersBarField label={t('Search coupons')}>
							<SearchInput
								variant="filled"
								label={t('Search coupons')}
								placeholder={t('Search')}
								onChange={handleSearch}
							/>
						</FiltersBarField>
						<FiltersBarField label={t('Status')}>
							<Select
								id="status"
								variant="filled"
								placeholder={t('All')}
								aria-label={t('Status')}
								options={statusOptions}
								value={
									statusOptions.find((option) => option.value === searchParams.get('status')) ??
									null
								}
								onChange={(data) => handleFilter({ id: 'status', value: data?.value || null })}
							/>
						</FiltersBarField>
						<FiltersBarField label={t('Area type')}>
							<Select
								id="booth_type"
								variant="filled"
								placeholder={t('All')}
								aria-label={t('Area type')}
								options={boothTypeOptions}
								value={
									boothTypeOptions.find(
										(option) => String(option.value) === searchParams.get('booth_type')
									) ?? null
								}
								handleInfiniteScroll={fetchNextAreaTypes}
								onChange={(data) =>
									handleFilter({ id: 'booth_type', value: data?.value ? String(data.value) : null })
								}
							/>
						</FiltersBarField>
					</FiltersBar>
					{renderContent()}
				</CardWrapper>
			</div>

			{isDeleteModalOpen && (
				<DeleteConfirmModal
					title={t('Delete coupon')}
					isOpened={isDeleteModalOpen}
					closeModal={closeDeleteModal}
					confirm={couponDeleteMutation}>
					<p>{t('You are about to remove a coupon. Are you sure you want to proceed?')}</p>
				</DeleteConfirmModal>
			)}
			{isBulkDeleteModalOpen && (
				<DeleteConfirmModal
					title={t('Delete selected coupons')}
					isOpened={isBulkDeleteModalOpen}
					closeModal={closeBulkDeleteModal}
					confirm={couponsDeleteMutation}>
					<p>
						{t('You are about to remove {{ count }} coupons. Are you sure you want to proceed?', {
							count: checkedCoupons.length
						})}
					</p>
				</DeleteConfirmModal>
			)}
			{isExportModalOpen && (
				<ConfirmModal
					title={t('Export coupons')}
					isOpened={isExportModalOpen}
					closeModal={closeExportModal}
					confirm={handleExportModal}>
					<p>{t('You are about to export coupons. Are you sure you want to proceed?')}</p>
				</ConfirmModal>
			)}
			{isFormatModalOpen && (
				<FormatModal
					disableButton={chosenExportFormat === null}
					isOpened={isFormatModalOpen}
					closeModal={closeFormatModal}
					confirm={() => exportCouponsMutation({ format: chosenExportFormat, ids: checkedCoupons })}
					isLoading={isExportCouponsLoading}
					onChange={handleFormatChange}
				/>
			)}
		</>
	)
}

export default CouponsPage
